import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { useListAdminTicketsQuery } from '../../features/tickets/ticketsApi';
import { TICKET_CATEGORIES } from '../../lib/validators/ticketSchema';
import Card, { CardHeader } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import TicketCard from '../../components/tickets/TicketCard';
import EmptyState from '../../components/ui/EmptyState';
import { MessageCircleIcon } from '../../components/ui/icons';
import { SkeletonCardGrid } from '../../components/ui/Skeleton';
import { authErrorMessage } from '../../lib/authErrorMessage';

const STATUS_TABS = [
  { value: '', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

const CATEGORY_OPTIONS = [{ value: '', label: 'All categories' }, ...TICKET_CATEGORIES];

/**
 * GET /admin/support-tickets — permission: manage-support. Shared queue —
 * every admin/super-admin with the permission sees the same list, there's
 * no "assigned to me" concept to build here.
 */
export default function Tickets() {
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useListAdminTicketsQuery({
    status: status || undefined,
    category: category || undefined,
    page,
  });

  const tickets = data?.tickets ?? [];
  const meta = data?.meta;
  const parseError = data?.parseError;

  const changeStatus = (value) => {
    setStatus(value);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-semibold text-foreground">Support tickets</h1>
          <Badge tone="success">Live</Badge>
        </div>
        <p className="text-foreground-muted text-sm mt-1">Shared queue — any admin can reply, resolve, or close.</p>
      </div>

      <Card>
        <CardHeader
          title="Queue"
          action={
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex gap-1 rounded-lg bg-background-elevated p-1">
                {STATUS_TABS.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => changeStatus(t.value)}
                    className={clsx(
                      'px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
                      status === t.value ? 'bg-accent-soft text-accent' : 'text-foreground-muted hover:text-foreground'
                    )}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <select
                value={category}
                onChange={(e) => {
                  setCategory(e.target.value);
                  setPage(1);
                }}
                className="rounded-lg bg-background-elevated border border-border px-3 py-1.5 text-xs text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
              >
                {CATEGORY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          }
        />

        {isLoading && <SkeletonCardGrid count={6} />}

        {isError && (
          <p className="py-8 text-center text-danger">
            {authErrorMessage(error, {
              forbidden: "Your admin account doesn't have the manage-support permission needed to view the ticket queue.",
              fallback: 'Could not load support tickets.',
            })}
          </p>
        )}

        {!isLoading && !isError && parseError && (
          <p className="py-8 text-center text-danger">Something went wrong loading this data. Check the console for details.</p>
        )}

        {!isLoading && !isError && !parseError && tickets.length === 0 && (
          <EmptyState icon={MessageCircleIcon} title="No tickets match this filter" body="Try a different status or category." />
        )}

        {!isLoading && !isError && !parseError && tickets.length > 0 && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {tickets.map((t) => (
                <TicketCard key={t.id} ticket={t} onClick={() => navigate(`/admin/tickets/${t.id}`)} showInvestor />
              ))}
            </div>

            {meta && meta.lastPage > 1 && (
              <div className="flex items-center justify-between pt-4 mt-4 border-t border-border">
                <p className="text-xs text-foreground-subtle">
                  Page {meta.currentPage} of {meta.lastPage} · {meta.total} total
                </p>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" disabled={meta.currentPage <= 1} onClick={() => setPage(Math.max(1, meta.currentPage - 1))}>
                    Previous
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={meta.currentPage >= meta.lastPage}
                    onClick={() => setPage(Math.min(meta.lastPage, meta.currentPage + 1))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
